import type { i18n } from 'next-i18next'
import type { NextRouter } from 'next/router'
import type { KeyedMutator } from 'swr'
import { changeLanguage } from 'shared/api/user-api'
import { LocaleEnum } from 'shared/enums/locale-enum'
import type { UnauthorizedUser, UserInterface } from 'shared/types/user-interface'
import { isClient } from 'shared/utils/is-client'
import { resolveLocaleInPath } from 'shared/utils/resolve-locale-in-path'
import { updateRuntimeLocale } from './update-runtime-locale'

type LocaleValue = LocaleEnum | string

const isLocaleEnum = (value: string): value is LocaleEnum =>
  (Object.values(LocaleEnum) as string[]).includes(value)
type UserMutator = KeyedMutator<UserInterface | UnauthorizedUser>
type RouterLike = Pick<NextRouter, 'asPath' | 'locale' | 'pathname' | 'push'>

interface ChangeLocaleOptions {
  locale?: LocaleValue
  i18n?: typeof i18n | null
  persistCookie?: boolean
  disableBackendUpdate?: boolean
  userDashboardLocale?: LocaleValue
  mutateUser?: UserMutator
  revalidateUser?: boolean
  router?: RouterLike
  forceRouterUpdate?: boolean
}

class LocaleChangeManager {
  private isChanging = false

  public get changeIsInProgress(): boolean {
    return this.isChanging
  }

  public async changeLocale({
    locale,
    i18n,
    persistCookie = true,
    disableBackendUpdate,
    userDashboardLocale,
    mutateUser,
    revalidateUser = true,
    router,
    forceRouterUpdate = false,
  }: ChangeLocaleOptions): Promise<{
    changed: boolean
  }> {
    if (!locale) return { changed: false }
    if (this.isChanging) return { changed: false }

    const clientSide = isClient()
    const localeString = String(locale)
    if (i18n && i18n.language === localeString) return { changed: false }

    this.isChanging = true
    try {
      await this.updateBackendLocale(localeString, {
        disableBackendUpdate,
        userDashboardLocale,
        clientSide,
      })

      const [changed] = await Promise.all([
        updateRuntimeLocale({
          i18n,
          locale: localeString,
          persistCookie,
        }),
        this.updateUserLocaleState(localeString, {
          mutateUser,
          revalidateUser,
          clientSide,
        }),
      ])

      await this.updateRouterLocale(localeString, {
        router,
        clientSide,
        forceRouterUpdate,
      })

      return { changed }
    } finally {
      this.isChanging = false
    }
  }

  private async updateBackendLocale(
    localeString: string,
    options: {
      disableBackendUpdate?: boolean
      userDashboardLocale?: LocaleValue
      clientSide: boolean
    },
  ): Promise<void> {
    const { disableBackendUpdate, userDashboardLocale, clientSide } = options
    const canUpdateBackend = !disableBackendUpdate && clientSide

    if (!canUpdateBackend) return

    const shouldUpdateBackend = !userDashboardLocale || userDashboardLocale !== localeString

    if (shouldUpdateBackend && isLocaleEnum(localeString)) {
      await changeLanguage(localeString)
    }
  }

  private async updateUserLocaleState(
    localeString: string,
    options: {
      mutateUser?: UserMutator
      revalidateUser: boolean
      clientSide: boolean
    },
  ): Promise<void> {
    const { mutateUser, revalidateUser, clientSide } = options

    if (!clientSide || !mutateUser) return

    await mutateUser(currentUser => {
      if (!currentUser) {
        return currentUser
      }

      if (currentUser.dashboardLocale === localeString) {
        return currentUser
      }

      if (!isLocaleEnum(localeString)) {
        return currentUser
      }

      return {
        ...currentUser,
        dashboardLocale: localeString,
      }
    }, revalidateUser)
  }

  private async updateRouterLocale(
    localeString: string,
    options: {
      router?: RouterLike
      clientSide: boolean
      forceRouterUpdate?: boolean
    },
  ): Promise<void> {
    const { router, clientSide, forceRouterUpdate } = options

    if (!clientSide || !router) return

    const { localeInPath, hasLocaleInPath } = resolveLocaleInPath(globalThis.location.pathname)

    if ((hasLocaleInPath || forceRouterUpdate) && localeInPath !== localeString) {
      try {
        await router.push(router.pathname, router.asPath, { locale: localeString })
      } catch (error) {
        throw new Error('Failed to update locale in URL', { cause: error })
      }
    }
  }
}

const localeChangeManager = new LocaleChangeManager()

export const changeLocale = (
  options: ChangeLocaleOptions,
): Promise<{
  changed: boolean
}> => localeChangeManager.changeLocale(options)
